'use client';

import {Button, Modal, Stack, Text} from '@eds-web/react';
import {useMessages} from '@epic-mw/i18n';
import {SyntheticEvent, useEffect, useState} from 'react';
import {ModalLoading} from '../loading/ModalLoading';
import {useDebounce} from '@/hooks';
import {AnalyticEventProps, KeyImageType} from '@/types';
import {emitEvent, useEmitEventOnClick} from '@/lib/utils/analytics';
import {CatalogOffer} from 'epic-graphql-types';
import {getOfferKeyImages} from '@/lib/utils/catalog.utils';
import {css} from '@panda/css';

const modalStyles = css({
	alignItems: 'center',
	textAlign: 'center',
	width: '100%',
	// borderTopWidth: "1px",
	// borderTopStyle: "solid",
	// borderImageSource: "linear-gradient(225deg, #F1B8DD 0.35%, #868AFD 100.35%)",
	// borderImageSlice: 1, // Required
	bg: '#101014 !important',
	'& > div': {
		bg: '#101014 !important',
		justifyContent: 'center !important'
	},
	smDown: {
		width: 'unset !important'
	}
});

interface PromotionModalProps extends CatalogOffer {
	open: boolean;
	isLoading?: boolean;
	analytics?: AnalyticEventProps;
	onClose?: () => void;
}

export const PromotionModal = ({
	open,
	namespace: sandboxId,
	id: offerId,
	locale,
	title,
	keyImages,
	isLoading,
	analytics,
	title4Sort,
	onClose
}: PromotionModalProps) => {
	const messages = useMessages();
	const [debouncing, setDebouncing] = useState(false);
	const images = getOfferKeyImages(keyImages, [KeyImageType.Thumbnail]);
	const thumbnail = images.get(KeyImageType.Thumbnail);
	const emitClickEvent = useEmitEventOnClick({
		...analytics!,
		eventValue: {
			...analytics?.eventValue,
			locale,
			sandboxId,
			offerId,
			title,
			title4Sort,
			thumbnailUrl: thumbnail?.url
		}
	});

	const handleContinue = useDebounce(<T extends HTMLElement>(e?: SyntheticEvent<T>) => {
		try {
			onClose?.();
			emitClickEvent(e, {status: 'initiate', continue: 'true'});
		} catch (err) {
			console.log('handle promotional continue failed with: ', err);
		}
		setDebouncing(false);
	});

	useEffect(() => {
		if (!open) return;
		emitEvent(
			{
				eventCategory: 'pageview',
				interactionType: 'pageview',
				eventLabel: 'PageView_V2',
				eventValue: {
					subView: 'Accept Promotions',
					locale,
					sandboxId,
					offerId,
					title,
					title4Sort,
					thumbnailUrl: thumbnail?.url
				}
			},
			'PageView'
		);
	}, [open]);

	return (
		<Modal
			className={modalStyles}
			hideXButton
			size="sm"
			width={480}
			containerProps={{className: css({zIndex: '10000 !important', maxWidth: '100%'})}}
			isOpen={open}
			onOpenChange={(isOpen) => {
				// seems like this is never called when `isOpen === true` :think:
				if (!isOpen) handleContinue?.();
			}}
			title={messages['epic.app.bonus.item.title']}
			aria-labelledby={messages['epic.app.bonus.item.title']}
			closeLabel={messages['epic.app.close'] || 'Close'}
			closeButton={undefined}
			content={
				<div style={{maxWidth: '352px'}}>
					{isLoading && <ModalLoading />}
					<Stack gap={4}>
						{thumbnail && (
							<img
								src={thumbnail.url!}
								alt={thumbnail.alt!}
								width={200}
								height="auto"
								style={{
									objectFit: 'cover',
									display: 'block',
									margin: '0 auto'
								}}
							/>
						)}

						<Text variant="uiLGBold">{messages['epic.app.bonus.item.received']}</Text>

						<Text variant="uiLGBold" style={{color: '#26BBFF'}}>
							{title}
						</Text>

						<Stack gap={8}>
							<Text variant="uiMD" color="secondary">
								{messages['epic.app.bonus.item.desc']}
							</Text>
						</Stack>
					</Stack>
				</div>
			}
			submitButton={
				<Button
					loadingLabel={messages['epic.app.continue']}
					loading={debouncing}
					disabled={isLoading}
					size="lg"
					onClick={(e) => {
						setDebouncing(true);
						handleContinue(e);
					}}>
					{messages['epic.app.continue']}
				</Button>
			}
		/>
	);
};
